/**
 * Bridge Multiplayer Mode
 * Real-time 4-player Bridge via WebSocket
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Users, Copy, Check, Wifi, WifiOff, Crown, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { POSITIONS, BID_LEVELS, BID_STRAINS, SPECIAL_BIDS, SUITS } from './bridgeConstants';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Position colors and labels
const POSITION_COLORS = {
  NORTH: 'text-blue-400',
  SOUTH: 'text-emerald-400',
  EAST: 'text-amber-400',
  WEST: 'text-rose-400'
};

const PARTNERSHIPS = {
  NS: ['NORTH', 'SOUTH'],
  EW: ['EAST', 'WEST']
};

// ==================== Card Component ====================
const Card = ({ card, onClick, disabled, small, faceDown }) => {
  if (faceDown || !card) {
    return (
      <div className={`
        ${small ? 'w-8 h-12' : 'w-12 h-16'} 
        rounded-lg bg-gradient-to-br from-blue-800 to-blue-950
        border border-blue-600 shadow-md
        flex items-center justify-center
      `}>
        <div className="w-6 h-6 rounded-full bg-blue-700 border border-blue-500" />
      </div>
    );
  }
  
  return (
    <motion.button
      whileHover={!disabled ? { y: -4, scale: 1.02 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
      onClick={() => !disabled && onClick?.(card)}
      disabled={disabled}
      className={`
        ${small ? 'w-8 h-12 text-[10px]' : 'w-12 h-16 text-xs'}
        rounded-lg bg-white shadow-md
        border ${disabled ? 'border-gray-300 opacity-60' : 'border-gray-200 hover:border-amber-400'}
        ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
        flex flex-col items-center justify-between p-0.5
        transition-all duration-100
      `}
    >
      <div className="font-bold" style={{ color: card.color }}>{card.display}</div>
      <div className={small ? 'text-sm' : 'text-lg'} style={{ color: card.color }}>{card.symbol}</div>
    </motion.button>
  );
};

// ==================== Lobby Component ====================
const BridgeLobby = ({ onJoinGame }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('menu'); // menu, create, join
  const [playerName, setPlayerName] = useState('');
  const [roomCode, setRoomCode] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('SOUTH');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [roomInfo, setRoomInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  // Check for room code in URL
  useEffect(() => {
    const roomFromUrl = searchParams.get('room');
    if (roomFromUrl) {
      setRoomCode(roomFromUrl);
      setMode('join');
      fetchRoomInfo(roomFromUrl);
    }
  }, [searchParams]);

  const fetchRoomInfo = async (code) => {
    try {
      const res = await fetch(`${API_URL}/api/bridge/room/${code}`);
      if (res.ok) {
        const data = await res.json();
        setRoomInfo(data);
      }
    } catch (e) {
      console.error('Failed to fetch room info:', e);
    }
  };

  const createRoom = async () => {
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }
    setLoading(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/bridge/create-room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host_name: playerName,
          preferred_position: selectedPosition
        })
      });
      
      if (!res.ok) throw new Error('Failed to create room');
      
      const data = await res.json();
      onJoinGame(data.room_id, selectedPosition, playerName);
    } catch (e) {
      setError('Failed to create room. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const joinRoom = async () => {
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!roomCode.trim()) {
      setError('Please enter room code');
      return;
    }
    setLoading(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/bridge/room/${roomCode}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player_name: playerName,
          preferred_position: selectedPosition !== 'auto' ? selectedPosition : null
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to join room');
      }
      
      const data = await res.json();
      onJoinGame(data.room_id, data.position, playerName);
    } catch (e) {
      setError(e.message || 'Failed to join room');
    } finally {
      setLoading(false);
    }
  };

  const copyInviteLink = () => {
    if (roomInfo?.room_id) {
      const link = `${window.location.origin}/games/bridge?room=${roomInfo.room_id}`;
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-900/90 backdrop-blur-sm rounded-2xl border border-slate-700 p-8 w-full max-w-md"
      >
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => mode === 'menu' ? navigate('/games/bridge') : setMode('menu')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Bridge Multiplayer</h1>
            <p className="text-sm text-gray-400">Play with friends online</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-900/50 border border-red-500/50 rounded-lg p-3 mb-4 text-red-200 text-sm">
            {error}
          </div>
        )}

        {/* Menu */}
        {mode === 'menu' && (
          <div className="space-y-4">
            <Button
              onClick={() => setMode('create')}
              className="w-full bg-emerald-600 hover:bg-emerald-500 h-14 text-lg"
            >
              <Crown className="w-5 h-5 mr-2" />
              Create Room
            </Button>
            <Button
              onClick={() => setMode('join')}
              variant="outline"
              className="w-full border-slate-600 h-14 text-lg"
            >
              <Users className="w-5 h-5 mr-2" />
              Join Room
            </Button>
          </div>
        )}

        {/* Create Room */}
        {mode === 'create' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 block mb-1">Your Name</label>
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Enter your name"
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white"
                maxLength={20}
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-400 block mb-1">Select Position</label>
              <div className="grid grid-cols-2 gap-2">
                {POSITIONS.map(pos => (
                  <button
                    key={pos}
                    onClick={() => setSelectedPosition(pos)}
                    className={`
                      p-3 rounded-lg border transition-all
                      ${selectedPosition === pos 
                        ? 'bg-emerald-600/20 border-emerald-500 text-emerald-400' 
                        : 'bg-slate-800 border-slate-600 text-gray-300 hover:border-slate-500'}
                    `}
                  >
                    {pos}
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={createRoom}
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-500 h-12"
            >
              {loading ? 'Creating...' : 'Create Room'}
            </Button>
          </div>
        )}

        {/* Join Room */}
        {mode === 'join' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 block mb-1">Your Name</label>
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Enter your name"
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white"
                maxLength={20}
              />
            </div>

            <div>
              <label className="text-sm text-gray-400 block mb-1">Room Code</label>
              <input
                type="text"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                placeholder="Enter room code"
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white font-mono"
                maxLength={12}
              />
            </div>

            {roomInfo && (
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h3 className="text-sm text-gray-400 mb-2">Players in Room:</h3>
                <div className="grid grid-cols-2 gap-2">
                  {POSITIONS.map(pos => (
                    <div 
                      key={pos}
                      className={`text-sm ${roomInfo.players?.[pos] ? POSITION_COLORS[pos] : 'text-gray-600'}`}
                    >
                      {pos}: {roomInfo.players?.[pos] || 'Empty'}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button
              onClick={joinRoom}
              disabled={loading}
              className="w-full bg-cyan-600 hover:bg-cyan-500 h-12"
            >
              {loading ? 'Joining...' : 'Join Room'}
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// ==================== Waiting Room ====================
const WaitingRoom = ({ roomId, position, playerName, players, onReady, onLeave }) => {
  const [copied, setCopied] = useState(false);
  const [isReady, setIsReady] = useState(false);
  
  const inviteLink = `${window.location.origin}/games/bridge?room=${roomId}`;
  
  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReady = () => {
    setIsReady(true);
    onReady();
  };

  const playerCount = Object.keys(players).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-900/90 backdrop-blur-sm rounded-2xl border border-slate-700 p-8 w-full max-w-lg"
      >
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-white mb-2">Waiting for Players</h1>
          <p className="text-gray-400">Room: <span className="font-mono text-cyan-400">{roomId}</span></p>
        </div>

        {/* Invite Link */}
        <div className="bg-slate-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-400 mb-2">Share this link:</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={inviteLink}
              readOnly
              className="flex-1 bg-slate-900 border border-slate-600 rounded px-3 py-2 text-sm text-white font-mono"
            />
            <Button
              onClick={copyLink}
              variant="outline"
              size="sm"
              className="border-slate-600"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Player List */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {POSITIONS.map(pos => {
            const player = players[pos];
            const isYou = pos === position;
            
            return (
              <div
                key={pos}
                className={`
                  p-4 rounded-lg border transition-all
                  ${player 
                    ? 'bg-slate-800/50 border-slate-600' 
                    : 'bg-slate-800/20 border-slate-700 border-dashed'}
                `}
              >
                <div className={`font-semibold mb-1 ${POSITION_COLORS[pos]}`}>
                  {pos}
                </div>
                {player ? (
                  <div className="flex items-center gap-2">
                    <span className="text-white">{player.name}</span>
                    {isYou && <span className="text-xs text-emerald-400">(You)</span>}
                    {player.ready && <Check className="w-4 h-4 text-emerald-400" />}
                  </div>
                ) : (
                  <span className="text-gray-500 text-sm">Waiting...</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            onClick={onLeave}
            variant="outline"
            className="flex-1 border-slate-600"
          >
            Leave
          </Button>
          <Button
            onClick={handleReady}
            disabled={isReady || playerCount < 4}
            className={`flex-1 ${isReady ? 'bg-emerald-600' : 'bg-amber-600 hover:bg-amber-500'}`}
          >
            {isReady ? 'Ready!' : playerCount < 4 ? `Waiting (${playerCount}/4)` : 'Ready'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// ==================== Multiplayer Game Component ====================
const MultiplayerGame = ({ roomId, position, playerName, gameState, onBid, onPlayCard, onNewHand }) => {
  const { 
    game_phase, hands, players, auction, current_bidder, contract, 
    declarer, dummy, current_trick, trick_number, tricks_won, 
    current_player, score, your_position
  } = gameState;

  const [selectedBid, setSelectedBid] = useState({ level: 1, strain: 'CLUBS' });

  // Your hand
  const myHand = Array.isArray(hands?.[your_position]) ? hands[your_position] : [];
  
  // Check if it's your turn
  const isMyTurn = game_phase === 'bidding' 
    ? current_bidder === your_position
    : current_player === your_position || (current_player === dummy && declarer === your_position);

  const handleBid = (bid) => {
    if (!isMyTurn || game_phase !== 'bidding') return;
    onBid(bid);
  };

  const handlePlayCard = (card) => {
    if (!isMyTurn || game_phase !== 'playing') return;
    onPlayCard(card);
  };

  const strainSymbols = {
    CLUBS: '♣', DIAMONDS: '♦', HEARTS: '♥', SPADES: '♠', NOTRUMP: 'NT'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">Bridge - Multiplayer</h2>
            <p className="text-sm text-gray-400">
              Room: {roomId} • You are {your_position}
            </p>
          </div>
          <div className="text-right">
            <div className="text-cyan-400 font-semibold">NS: {score?.NS || 0}</div>
            <div className="text-amber-400 font-semibold">EW: {score?.EW || 0}</div>
          </div>
        </div>

        {/* Game Table */}
        <div className="bg-emerald-900/30 rounded-2xl border border-emerald-700/30 p-6 relative min-h-[500px]">
          {/* Status Message */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
            <div className="bg-slate-900/90 px-4 py-2 rounded-full border border-slate-700">
              <p className="text-white text-sm">
                {game_phase === 'bidding' && `${current_bidder}'s bid`}
                {game_phase === 'playing' && `Trick ${trick_number} - ${current_player}'s turn`}
                {game_phase === 'scoring' && 'Hand Complete!'}
              </p>
            </div>
          </div>

          {/* Player Positions */}
          {POSITIONS.map(pos => {
            const player = players?.[pos];
            const isMe = pos === your_position;
            const isDummy = pos === dummy && game_phase === 'playing';
            const handData = hands?.[pos];
            const cardCount = typeof handData === 'number' ? handData : (handData?.length || 0);
            const showCards = isMe || isDummy;
            
            const positionStyle = {
              NORTH: 'top-16 left-1/2 -translate-x-1/2',
              SOUTH: 'bottom-8 left-1/2 -translate-x-1/2',
              WEST: 'left-4 top-1/2 -translate-y-1/2',
              EAST: 'right-4 top-1/2 -translate-y-1/2'
            };

            return (
              <div key={pos} className={`absolute ${positionStyle[pos]}`}>
                <div className="text-center mb-2">
                  <span className={`text-xs font-semibold ${POSITION_COLORS[pos]}`}>
                    {pos} {isDummy && '(Dummy)'}
                  </span>
                  <span className="text-gray-400 text-xs ml-2">{player?.name}</span>
                  {(pos === current_bidder || pos === current_player) && (
                    <span className="text-amber-400 text-xs ml-1">●</span>
                  )}
                </div>
                
                {/* Cards */}
                <div className={`flex ${pos === 'WEST' || pos === 'EAST' ? 'flex-col -space-y-6' : 'flex-row -space-x-2'}`}>
                  {showCards && Array.isArray(handData) ? (
                    handData.map((card, idx) => (
                      <div key={card.id} style={{ zIndex: idx }}>
                        <Card
                          card={card}
                          onClick={handlePlayCard}
                          disabled={!isMyTurn || game_phase !== 'playing' || (!isMe && !(isDummy && declarer === your_position))}
                          small={!isMe}
                        />
                      </div>
                    ))
                  ) : (
                    Array(cardCount).fill(0).map((_, idx) => (
                      <div key={idx} style={{ zIndex: idx }}>
                        <Card faceDown small={!isMe} />
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}

          {/* Center - Trick/Bidding Area */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            {game_phase === 'playing' && (
              <div className="w-40 h-28 bg-emerald-800/50 rounded-xl border border-emerald-600/30 relative">
                {current_trick?.map(({ player, card }) => {
                  const trickPos = {
                    SOUTH: 'bottom-1 left-1/2 -translate-x-1/2',
                    NORTH: 'top-1 left-1/2 -translate-x-1/2',
                    WEST: 'left-1 top-1/2 -translate-y-1/2',
                    EAST: 'right-1 top-1/2 -translate-y-1/2'
                  };
                  return (
                    <div key={card.id} className={`absolute ${trickPos[player]}`}>
                      <Card card={card} small disabled />
                    </div>
                  );
                })}
                {(!current_trick || current_trick.length === 0) && (
                  <div className="absolute inset-0 flex items-center justify-center text-emerald-600/50 text-xs">
                    Play Area
                  </div>
                )}
              </div>
            )}

            {game_phase === 'bidding' && isMyTurn && (
              <div className="bg-slate-900/90 rounded-xl p-4 border border-slate-700">
                <h3 className="text-amber-400 text-sm font-semibold mb-2">Your Bid</h3>
                <div className="flex gap-1 mb-2">
                  {BID_LEVELS.map(level => (
                    <button
                      key={level}
                      onClick={() => setSelectedBid(b => ({ ...b, level }))}
                      className={`w-7 h-7 rounded text-sm font-bold ${
                        selectedBid.level === level 
                          ? 'bg-amber-500 text-white' 
                          : 'bg-slate-700 text-gray-300'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
                <div className="flex gap-1 mb-3">
                  {BID_STRAINS.map(strain => (
                    <button
                      key={strain}
                      onClick={() => setSelectedBid(b => ({ ...b, strain }))}
                      className={`w-8 h-8 rounded font-bold ${
                        selectedBid.strain === strain ? 'ring-2 ring-amber-400' : ''
                      } bg-slate-700`}
                    >
                      {strainSymbols[strain]}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleBid(selectedBid)}
                    className="bg-amber-600 hover:bg-amber-500 flex-1"
                  >
                    Bid
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBid('PASS')}
                    className="border-gray-600"
                  >
                    Pass
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Tricks Won Display */}
          <div className="absolute bottom-4 left-4 text-xs">
            <div className="text-gray-400">Tricks: NS {tricks_won?.NS || 0} - EW {tricks_won?.EW || 0}</div>
            {contract && (
              <div className="text-amber-400">
                Contract: {contract.level}{strainSymbols[contract.strain]}
              </div>
            )}
          </div>
        </div>

        {/* Auction History */}
        {auction && auction.length > 0 && (
          <div className="mt-4 bg-slate-900/50 rounded-lg p-4 border border-slate-700">
            <h3 className="text-sm text-gray-400 mb-2">Auction</h3>
            <div className="flex flex-wrap gap-2">
              {auction.map((entry, idx) => (
                <div key={idx} className={`text-sm ${POSITION_COLORS[entry.player]}`}>
                  {entry.player}: {
                    entry.bid === 'PASS' ? 'Pass' :
                    entry.bid === 'DOUBLE' ? 'X' :
                    entry.bid === 'REDOUBLE' ? 'XX' :
                    `${entry.bid.level}${strainSymbols[entry.bid.strain]}`
                  }
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scoring Overlay */}
        {game_phase === 'scoring' && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-slate-900 border border-amber-500/30 rounded-2xl p-8 max-w-md text-center"
            >
              <h2 className="text-2xl font-bold text-white mb-4">Hand Complete!</h2>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-800 rounded-xl p-4">
                  <div className="text-3xl font-bold text-cyan-400">{score?.NS || 0}</div>
                  <div className="text-sm text-gray-400">North-South</div>
                  <div className="text-xs text-gray-500">{tricks_won?.NS || 0} tricks</div>
                </div>
                <div className="bg-slate-800 rounded-xl p-4">
                  <div className="text-3xl font-bold text-amber-400">{score?.EW || 0}</div>
                  <div className="text-sm text-gray-400">East-West</div>
                  <div className="text-xs text-gray-500">{tricks_won?.EW || 0} tricks</div>
                </div>
              </div>
              <Button onClick={onNewHand} className="bg-amber-600 hover:bg-amber-500 w-full">
                Play Another Hand
              </Button>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== Main Multiplayer Wrapper ====================
const BridgeMultiplayer = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState('lobby'); // lobby, waiting, playing
  const [roomId, setRoomId] = useState('');
  const [position, setPosition] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [gameState, setGameState] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);

  const connectToRoom = useCallback((roomId, pos, name) => {
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/bridge/${roomId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected');
      // Send position identification
      ws.send(JSON.stringify({ position: pos }));
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('WS message:', data.type);

      switch (data.type) {
        case 'connected':
          setConnected(true);
          setGameState(data.state);
          if (data.state.game_phase === 'waiting') {
            setStage('waiting');
          } else {
            setStage('playing');
          }
          break;

        case 'state_update':
          setGameState(data.state);
          if (data.state.game_phase !== 'waiting') {
            setStage('playing');
          }
          break;

        case 'player_connected':
        case 'player_disconnected':
          // State update will follow
          break;

        case 'trick_complete':
          // Visual feedback could be added here
          break;

        case 'error':
          console.error('Server error:', data.message);
          break;
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setConnected(false);
    };

    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
    };
  }, []);

  const handleJoinGame = (roomId, pos, name) => {
    setRoomId(roomId);
    setPosition(pos);
    setPlayerName(name);
    connectToRoom(roomId, pos, name);
  };

  const handleReady = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ready' }));
    }
  };

  const handleLeave = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    setStage('lobby');
    setGameState(null);
    navigate('/games/bridge');
  };

  const handleBid = (bid) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'bid', bid }));
    }
  };

  const handlePlayCard = (card) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'play_card', card }));
    }
  };

  const handleNewHand = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'new_hand' }));
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  if (stage === 'lobby') {
    return <BridgeLobby onJoinGame={handleJoinGame} />;
  }

  if (stage === 'waiting' && gameState) {
    return (
      <WaitingRoom
        roomId={roomId}
        position={position}
        playerName={playerName}
        players={gameState.players || {}}
        onReady={handleReady}
        onLeave={handleLeave}
      />
    );
  }

  if (stage === 'playing' && gameState) {
    return (
      <MultiplayerGame
        roomId={roomId}
        position={position}
        playerName={playerName}
        gameState={gameState}
        onBid={handleBid}
        onPlayCard={handlePlayCard}
        onNewHand={handleNewHand}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="text-white">Connecting...</div>
    </div>
  );
};

export default BridgeMultiplayer;
